import { useEffect, useMemo, useRef, useState, type ChangeEvent, type MouseEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PlusIcon, SymbolIcon } from "@radix-ui/react-icons";
import {
  ArrowDownRight,
  ArrowUpRight,
  BadgeCheck,
  Building2,
  Copy,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Download,
  FileText,
  History as HistoryIcon,
  KeyRound,
  Replace,
  RefreshCcw,
  Search,
  ShieldCheck,
  Star,
  UserRound,
  Upload,
  X,
} from "lucide-react";

import { Button } from "./common/ui/button";
import { Input } from "./common/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./common/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./common/ui/table";
import { useToast } from "./common/ui/use-toast";
import { Statuses } from "src/helpers/constants";
import {
  capitalizeFirstLetter,
  formatAmount,
  getWalletBalance,
  getWalletBlockedBalance,
  getWalletLabel,
  getWalletNumber,
  normalizeWalletList,
  type WalletLike,
} from "src/helpers/utils";
import {
  createBusiness,
  getBusinesses,
  getBusinessTypes,
  getBusinessWallet,
  getCountries,
  getSelectedBusiness,
  getAllTransferHistory,
  getTransferHistory,
  getUser,
  uploadBusinessDocs,
  setDefaultBusiness,
  unsetDefaultBusiness,
} from "../services";
import { REACT_APP_BASE_URL } from "src/helpers/constants";
import AddBusiness from "./AddBusiness";
import TransferDetails from "./common/TransferDetails";
import InitiateTransfer from "./common/InitiateTransfer";
import BulkTransfer from "./common/BulkTransfer";
import BusinessTradeProfile from "./common/BusinessTradeProfile";
import MYAPIs from "./MYAPIs";
import Banner from "./common/Banner";
import { LoadingContainer, Title, TitleDescription, TitleWrapper, Wrapper } from "./Styles";

const hasStoredAuth = () => Boolean(localStorage.getItem("access") || localStorage.getItem("refresh"));

type BusinessItem = {
  id: string;
  name?: string;
  email?: string;
  country?: string;
  description?: string;
  phone?: string;
  type?: string;
  size?: string | number;
  transaction_volume?: string;
  address?: string;
  status?: string;
  is_approved?: boolean;
  is_email_verified?: boolean;
  is_default?: boolean;
  id_director_one?: string;
  id_director_two?: string;
  business_documents?: {
    registration_documents?: string;
    id_image_director_one?: string;
    id_image_director_two?: string;
    logo?: string;
  };
};

type BusinessTransaction = {
  id?: string | number;
  transaction_type?: string;
  amount?: string | number;
  currency_code?: string;
  wallet_number?: string;
  channel?: string;
  debit_or_credit?: string;
  status?: string;
  charges?: string | number;
  reference?: string;
  created_at?: string;
  description?: string;
  sender_wallet_number?: string;
  receiver_wallet_number?: string;
  sender_id?: string | number;
  receiver_id?: string | number;
};

type HistoryFilters = {
  query: string;
  status: string;
  start_date: string;
  end_date: string;
};

type BusinessTab = "trade-profile" | "transactions" | "kyc" | "api-keys";

type BusinessDocumentField =
  | "registration_documents"
  | "id_image_director_one"
  | "id_image_director_two"
  | "logo";

type BusinessDocumentCard = {
  label: string;
  field: BusinessDocumentField;
  description: string;
  value?: string;
};

type BusinessWalletCardTheme = {
  helper: string;
  number: string;
  chip: string;
  chipTone: string;
  border: string;
  card: string;
  iconBg: string;
  accentBar: string;
};

const businessThemes = [
  {
    card: "bg-white text-slate-900",
    helper: "text-rose-500",
    number: "text-slate-900",
    border: "border-rose-200",
    chip: "bg-rose-50 text-rose-700",
    chipTone: "text-rose-500",
    iconBg: "bg-rose-100 text-rose-600",
    accentBar: "bg-rose-400",
  },
  {
    card: "bg-white text-slate-900",
    helper: "text-slate-500",
    number: "text-slate-900",
    border: "border-slate-200",
    chip: "bg-slate-50 text-slate-700",
    chipTone: "text-slate-500",
    iconBg: "bg-slate-100 text-slate-700",
    accentBar: "bg-slate-300",
  },
  {
    card: "bg-white text-slate-900",
    helper: "text-indigo-500",
    number: "text-slate-900",
    border: "border-indigo-200",
    chip: "bg-indigo-50 text-indigo-700",
    chipTone: "text-indigo-500",
    iconBg: "bg-indigo-100 text-indigo-600",
    accentBar: "bg-indigo-300",
  },
  {
    card: "bg-white text-slate-900",
    helper: "text-emerald-500",
    number: "text-slate-900",
    border: "border-emerald-200",
    chip: "bg-emerald-50 text-emerald-700",
    chipTone: "text-emerald-500",
    iconBg: "bg-emerald-100 text-emerald-600",
    accentBar: "bg-emerald-300",
  },
];

const walletCardThemes = [
  {
    shell: "bg-rose-100/95 text-slate-900",
    border: "border-rose-200",
    helper: "text-rose-600",
    title: "text-slate-900",
    chip: "bg-white text-rose-700 ring-1 ring-rose-200",
    tile: "bg-white/92 ring-1 ring-rose-100",
    tileLabel: "text-slate-500",
    tileValue: "text-slate-900",
    copyButton: "bg-white text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50",
    accentBar: "bg-rose-400",
  },
  {
    shell: "bg-slate-100/95 text-slate-900",
    border: "border-slate-200",
    helper: "text-slate-600",
    title: "text-slate-900",
    chip: "bg-white text-slate-700 ring-1 ring-slate-200",
    tile: "bg-white/92 ring-1 ring-slate-200",
    tileLabel: "text-slate-500",
    tileValue: "text-slate-900",
    copyButton: "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50",
    accentBar: "bg-slate-400",
  },
  {
    shell: "bg-indigo-100/95 text-slate-900",
    border: "border-indigo-200",
    helper: "text-indigo-600",
    title: "text-slate-900",
    chip: "bg-white text-indigo-700 ring-1 ring-indigo-200",
    tile: "bg-white/92 ring-1 ring-indigo-100",
    tileLabel: "text-slate-500",
    tileValue: "text-slate-900",
    copyButton: "bg-white text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-50",
    accentBar: "bg-indigo-400",
  },
  {
    shell: "bg-emerald-100/95 text-slate-900",
    border: "border-emerald-200",
    helper: "text-emerald-600",
    title: "text-slate-900",
    chip: "bg-white text-emerald-700 ring-1 ring-emerald-200",
    tile: "bg-white/92 ring-1 ring-emerald-100",
    tileLabel: "text-slate-500",
    tileValue: "text-slate-900",
    copyButton: "bg-white text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-50",
    accentBar: "bg-emerald-400",
  },
];

const businessDocumentLabelMap: Record<BusinessDocumentField, string> = {
  registration_documents: "Registration document",
  id_image_director_one: "Director I ID",
  id_image_director_two: "Director II ID",
  logo: "Logo",
};

const businessDocumentDescriptionMap: Record<BusinessDocumentField, string> = {
  registration_documents: "Upload the official registration certificate or incorporation paper.",
  id_image_director_one: "Upload a clear government-issued ID for the first director.",
  id_image_director_two: "Upload a clear government-issued ID for the second director.",
  logo: "Upload the business logo used on the profile and public brand assets.",
};

const statusOptions = [{ label: "All", value: "all" }, ...Statuses.filter(({ value }) => value !== "all")];

const formatDate = (value?: string) => {
  if (!value) {
    return "N/A";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getStatusTone = (status?: string) => {
  const normalized = (status || "").toLowerCase();

  if (normalized.includes("complete") || normalized.includes("success")) {
    return "bg-emerald-50 text-emerald-700 ring-emerald-100";
  }

  if (normalized.includes("pending")) {
    return "bg-amber-50 text-amber-700 ring-amber-100";
  }

  if (normalized.includes("fail") || normalized.includes("cancel")) {
    return "bg-rose-50 text-rose-700 ring-rose-100";
  }

  return "bg-slate-100 text-slate-600 ring-slate-200";
};

const getBusinessWalletNumber = (transaction: BusinessTransaction) => {
  if (transaction.wallet_number) {
    return transaction.wallet_number;
  }

  const direction = (transaction.debit_or_credit || "").toLowerCase();
  if (direction.includes("debit")) {
    return transaction.receiver_wallet_number || transaction.sender_wallet_number || "N/A";
  }

  if (direction.includes("credit")) {
    return transaction.sender_wallet_number || transaction.receiver_wallet_number || "N/A";
  }

  return transaction.sender_wallet_number || transaction.receiver_wallet_number || "N/A";
};

const getModeLabel = (transaction: BusinessTransaction) => {
  const direction = (transaction.debit_or_credit || "").toLowerCase();

  if (direction.includes("debit")) {
    return "Debit";
  }

  if (direction.includes("credit")) {
    return "Credit";
  }

  return transaction.debit_or_credit || "Transfer";
};

const getTypeLabel = (transaction: BusinessTransaction) =>
  transaction.transaction_type || transaction.reference || transaction.description || "Transfer";

const getReferenceLabel = (transaction: BusinessTransaction) =>
  transaction.reference || transaction.description || transaction.channel || "Business transfer";

const getBusinessVerificationMeta = (business?: Pick<
  BusinessItem,
  "status" | "is_approved" | "is_email_verified"
>) => {
  const normalizedStatus = (business?.status || "").toLowerCase();

  if (["blocked", "suspended", "deleted"].includes(normalizedStatus)) {
    return {
      label: capitalizeFirstLetter(normalizedStatus || "blocked"),
      tone: "bg-rose-50 text-rose-700 ring-rose-100",
      Icon: ShieldCheck,
    };
  }

  if (normalizedStatus === "inactive") {
    return {
      label: "Inactive",
      tone: "bg-slate-100 text-slate-600 ring-slate-200",
      Icon: Clock3,
    };
  }

  if (normalizedStatus === "active" || normalizedStatus === "approved" || business?.is_approved) {
    return {
      label: "Verified",
      tone: "bg-emerald-50 text-emerald-700 ring-emerald-100",
      Icon: BadgeCheck,
    };
  }

  if (!business?.is_email_verified) {
    return {
      label: "Unverified",
      tone: "bg-amber-50 text-amber-700 ring-amber-100",
      Icon: Clock3,
    };
  }

  return {
    label: "Pending review",
    tone: "bg-amber-50 text-amber-700 ring-amber-100",
    Icon: Clock3,
  };
};

const escapeCsvValue = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const BusinessWalletPreview = ({
  businessId,
  theme,
}: {
  businessId: string;
  theme: BusinessWalletCardTheme;
}) => {
  const { toast } = useToast();

  const { data: businessWalletData, isLoading } = useQuery({
    queryKey: ["businessCardWallet", businessId],
    queryFn: () => getBusinessWallet(businessId),
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000,
  });

  const walletNumber =
    businessWalletData?.[0]?.number ||
    businessWalletData?.[0]?.wallet_number ||
    businessWalletData?.[0]?.wallet_no ||
    "";

  const handleCopyWallet = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (!walletNumber) {
      return;
    }

    try {
      await navigator.clipboard.writeText(String(walletNumber));
      toast({
        title: "Copied",
        description: "Business wallet number copied to clipboard.",
      });
    } catch {
      toast({
        variant: "destructive",
        title: "Copy failed",
        description: "Unable to copy the business wallet number.",
      });
    }
  };

  return (
    <div className="min-w-0">
      <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${theme.helper}`}>
        AKL wallet
      </p>
      <div className="mt-1 flex items-center gap-2">
        <p className={`max-w-[170px] truncate font-mono text-sm font-semibold ${theme.number}`}>
          {isLoading && !walletNumber ? "Loading..." : walletNumber || "Unavailable"}
        </p>
        <button
          type="button"
          onClick={handleCopyWallet}
          disabled={!walletNumber}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] transition ${
            theme.chip
          } ${walletNumber ? "hover:opacity-90" : "cursor-not-allowed opacity-50"}`}
        >
          <Copy className="h-3.5 w-3.5" />
          Copy
        </button>
      </div>
    </div>
  );
};

const SelectedBusinessWalletCards = ({
  businessId,
  theme,
}: {
  businessId: string;
  theme: BusinessWalletCardTheme;
}) => {
  const { toast } = useToast();

  const {
    data: businessWalletData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["selectedBusinessWallets", businessId],
    queryFn: () => getBusinessWallet(businessId),
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000,
  });

  const businessWallets = normalizeWalletList<WalletLike>(businessWalletData);

  const handleCopyWallet = async (walletNumber?: string | number) => {
    const value = String(walletNumber ?? "").trim();

    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      toast({
        title: "Copied",
        description: "Business wallet number copied to clipboard.",
      });
    } catch {
      toast({
        variant: "destructive",
        title: "Copy failed",
        description: "Unable to copy the business wallet number.",
      });
    }
  };

  return (
    <section className="mt-5 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className={`text-[11px] font-semibold uppercase tracking-[0.28em] ${theme.helper}`}>
            Wallets
          </p>
          <h3 className="mt-2 text-xl font-bold text-slate-900">Business wallets</h3>
        </div>
        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${theme.chip}`}>
          {businessWallets.length} wallet{businessWallets.length === 1 ? "" : "s"}
        </span>
      </div>

      {isLoading && !businessWalletData ? (
        <LoadingContainer>
          <p>Loading business wallets...</p>
        </LoadingContainer>
      ) : isError && !businessWalletData ? (
        <LoadingContainer>
          <p>Could not load business wallets.</p>
          <button onClick={() => refetch()} className="text-rose-600">
            Click to retry
          </button>
        </LoadingContainer>
      ) : businessWallets.length ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {businessWallets.map((wallet, index) => {
            const walletNumber = getWalletNumber(wallet);
            const walletLabel = getWalletLabel(wallet, index);
            const balance = getWalletBalance(wallet);
            const currency = wallet.currency ?? wallet.currency_value ?? wallet.currency_code ?? "";
            const cardTheme = walletCardThemes[index % walletCardThemes.length];

            return (
              <article
                key={wallet.id ?? walletNumber ?? index}
                className={`relative overflow-hidden rounded-[28px] border p-5 shadow-[0_18px_40px_rgba(15,23,42,0.06)] ${cardTheme.shell} ${cardTheme.border}`}
              >
                <div className={`absolute inset-x-0 top-0 h-1.5 ${cardTheme.accentBar}`} />
                <div className="relative">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.24em] ${cardTheme.helper}`}>
                      {index === 0 ? "Primary wallet" : `Wallet ${index + 1}`}
                    </p>
                    <h4 className={`mt-2 text-lg font-bold ${cardTheme.title}`}>{walletLabel}</h4>
                  </div>

                  {currency ? (
                    <span
                      className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${cardTheme.chip}`}
                    >
                      {currency}
                    </span>
                  ) : null}
                </div>

                <div className="mt-5 grid gap-3">
                  <div className={`overflow-hidden rounded-2xl p-4 ${cardTheme.tile}`}>
                    <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${cardTheme.tileLabel}`}>
                      Available
                    </p>
                    <p className={`mt-2 truncate text-2xl font-black tracking-tight ${cardTheme.tileValue}`}>{balance}</p>
                    {getWalletBlockedBalance(wallet) > 0 && (
                      <p className={`mt-1 text-[11px] font-medium ${cardTheme.tileLabel}`}>
                        In trade: {formatAmount(getWalletBlockedBalance(wallet))}
                      </p>
                    )}
                  </div>

                  <div className={`rounded-2xl p-4 ${cardTheme.tile}`}>
                    <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${cardTheme.tileLabel}`}>
                      Wallet number
                    </p>
                    <p className={`mt-2 break-all font-mono text-sm font-semibold ${cardTheme.tileValue}`}>
                      {walletNumber || "Unavailable"}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between gap-3">
                  <p className={`text-xs ${cardTheme.helper}`}>
                    {wallet.currency_code ? `Currency code: ${wallet.currency_code}` : "Business wallet"}
                  </p>

                  <button
                    type="button"
                    onClick={() => handleCopyWallet(walletNumber)}
                    disabled={!walletNumber}
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] transition ${
                      walletNumber
                        ? cardTheme.copyButton
                        : "cursor-not-allowed bg-white/10 text-white/45"
                    }`}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Copy
                  </button>
                </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <LoadingContainer>
          <p>No wallets found for this business.</p>
        </LoadingContainer>
      )}
    </section>
  );
};

const Business = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [showAddBusiness, setShowAddBusiness] = useState(false);
  const [openInitiateTransfer, setOpenInitiateTransfer] = useState(false);
  const [openBulkTransfer, setOpenBulkTransfer] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [showBusinessList, setShowBusinessList] = useState(false);
  const [activeTab, setActiveTab] = useState<BusinessTab>("transactions");
  const [kycStep, setKycStep] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyFilters, setHistoryFilters] = useState<HistoryFilters>({
    query: "",
    status: "all",
    start_date: "",
    end_date: "",
  });
  const [isExportingHistory, setIsExportingHistory] = useState(false);
  const [openTransferDetail, setOpenTransferDetail] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<BusinessTransaction | null>(null);
  const [uploadingDocumentField, setUploadingDocumentField] = useState<BusinessDocumentField | null>(null);
  const documentInputRefs = useRef<Partial<Record<BusinessDocumentField, HTMLInputElement | null>>>({});

  const {
    data: userData,
    isLoading: userIsLoading,
    isError: userIsError,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ["userDetails"],
    queryFn: () => getUser(),
    enabled: hasStoredAuth(),
  });

  const handleGetBusinessData = async (userId: string) => {
    try {
      return await getBusinesses(userId);
    } catch {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load business.",
      });
      return [];
    }
  };

  const {
    data: businessData,
    refetch: refetchBusinesses,
    isLoading: businessesIsLoading,
    isError: businessesIsError,
  } = useQuery({
    queryKey: ["businessDetails", userData?.id],
    queryFn: () => handleGetBusinessData(userData?.id),
    enabled: !!userData?.id,
  });

  const businessList = useMemo(() => (businessData ?? []) as BusinessItem[], [businessData]);
  const defaultBusiness = useMemo(
    () => businessList.find((business) => business.is_default),
    [businessList],
  );
  const selectedBusinessIndex = Math.max(
    0,
    businessList.findIndex((business) => String(business.id) === selectedBusinessId),
  );
  const selectedBusinessTheme = businessThemes[selectedBusinessIndex % businessThemes.length];

  useEffect(() => {
    if (!businessList.length) {
      return;
    }

    const preferredBusinessId = defaultBusiness ? String(defaultBusiness.id) : String(businessList[0].id);
    const selectedBusinessExists = businessList.some(
      (business) => String(business.id) === selectedBusinessId,
    );

    if (!selectedBusinessId && preferredBusinessId) {
      setSelectedBusinessId(preferredBusinessId);
      return;
    }

    if (!selectedBusinessExists && preferredBusinessId) {
      setSelectedBusinessId(preferredBusinessId);
    }
  }, [businessList, defaultBusiness, selectedBusinessId]);

  const selectedBusiness = useMemo(
    () => businessList.find((business) => String(business.id) === selectedBusinessId),
    [businessList, selectedBusinessId],
  );
  const selectedBusinessVerification = getBusinessVerificationMeta(selectedBusiness);

  const {
    data: selectedBusinessDetails,
    isLoading: selectedBusinessLoading,
    isError: selectedBusinessError,
    refetch: refetchSelectedBusiness,
  } = useQuery({
    queryKey: ["selectedBusinessDetails", selectedBusinessId],
    queryFn: () => getSelectedBusiness(selectedBusinessId),
    enabled: activeTab === "kyc" && !!selectedBusinessId,
  });

  const { data: businessWalletsForHistory } = useQuery({
    queryKey: ["businessWalletsForHistory", selectedBusinessId],
    queryFn: () => getBusinessWallet(selectedBusinessId),
    enabled: !!selectedBusinessId,
    staleTime: 5 * 60 * 1000,
  });

  const businessWalletNumbers = useMemo(() => {
    const wallets = normalizeWalletList<WalletLike>(businessWalletsForHistory);
    return wallets
      .map((w) => getWalletNumber(w))
      .filter((n): n is string => !!n)
      .join(",");
  }, [businessWalletsForHistory]);

  const {
    data: transferHistory,
    isLoading: historyIsLoading,
    isError: historyIsError,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: [
      "businessHistory",
      selectedBusinessId,
      historyPage,
      historyFilters.query,
      historyFilters.status,
      historyFilters.start_date,
      historyFilters.end_date,
      businessWalletNumbers,
    ],
    queryFn: () =>
      getTransferHistory(userData?.id, historyPage, {
        query: historyFilters.query || undefined,
        status: historyFilters.status,
        start_date: historyFilters.start_date || undefined,
        end_date: historyFilters.end_date || undefined,
        business_id: selectedBusinessId,
        wallet_numbers: businessWalletNumbers || undefined,
      }),
    enabled: activeTab === "transactions" && !!selectedBusinessId && !!userData?.id,
  });

  const historyRows = (transferHistory?.results ?? []) as BusinessTransaction[];
  const totalPages = Math.max(1, Number(transferHistory?.total_pages ?? 1));
  const hasNext = Boolean(transferHistory?.next);
  const hasPrev = Boolean(transferHistory?.previous);

  const {
    data: businessTypes,
    isLoading: businessTypesIsLoading,
  } = useQuery({
    queryKey: ["businessTypes"],
    queryFn: getBusinessTypes,
  });

  const {
    data: countries,
    isLoading: countriesIsLoading,
  } = useQuery({
    queryKey: ["countries"],
    queryFn: () => getCountries(),
  });

  const isPendingActivation = !(
    userData?.user_images?.profile_picture && userData?.user_images?.user_id_image
  );

  useEffect(() => {
    if (historyPage > totalPages) {
      setHistoryPage(totalPages);
    }
  }, [historyPage, totalPages]);

  useEffect(() => {
    setKycStep(1);
  }, [selectedBusinessId]);

  useEffect(() => {
    setHistoryPage(1);
    setOpenTransferDetail(false);
    setSelectedTransaction(null);
  }, [
    selectedBusinessId,
    historyFilters.query,
    historyFilters.status,
    historyFilters.start_date,
    historyFilters.end_date,
  ]);

  const selectedBusinessDetailsView = (selectedBusinessDetails ?? selectedBusiness) as BusinessItem | undefined;
  const isDefaultBusiness = Boolean(selectedBusinessDetailsView?.is_default ?? selectedBusiness?.is_default);
  const businessDocuments = selectedBusinessDetailsView?.business_documents ?? {};
  const documentCards: BusinessDocumentCard[] = [
    {
      label: businessDocumentLabelMap.registration_documents,
      field: "registration_documents",
      description: businessDocumentDescriptionMap.registration_documents,
      value: businessDocuments.registration_documents,
    },
    {
      label: businessDocumentLabelMap.id_image_director_one,
      field: "id_image_director_one",
      description: businessDocumentDescriptionMap.id_image_director_one,
      value: businessDocuments.id_image_director_one,
    },
    {
      label: businessDocumentLabelMap.id_image_director_two,
      field: "id_image_director_two",
      description: businessDocumentDescriptionMap.id_image_director_two,
      value: businessDocuments.id_image_director_two,
    },
    {
      label: businessDocumentLabelMap.logo,
      field: "logo",
      description: businessDocumentDescriptionMap.logo,
      value: businessDocuments.logo,
    },
  ];
  const documentsUploaded = documentCards.filter((item) => Boolean(item.value)).length;
  const totalKycSteps = 3;
  const kycStepItems = [
    {
      id: 1,
      title: "Business profile",
      description: "Review the core business details and contact information.",
      icon: Building2,
    },
    {
      id: 2,
      title: "Documents",
      description: "Inspect uploaded KYC documents and supporting files.",
      icon: FileText,
    },
    {
      id: 3,
      title: "Verification",
      description: "Confirm completion and open the full KYC page.",
      icon: BadgeCheck,
    },
  ];

  const createBusinessMutation = useMutation({
    mutationKey: ["createBusiness"],
    mutationFn: (payload: any) => createBusiness(userData?.id, payload),
    onSuccess: async () => {
      toast({
        description: "Business has been created successfully.",
      });
      setShowAddBusiness(false);
      await refetchBusinesses();
    },
    onError: (error: any) => {
      const errorMessages = error?.response?.data && Object.values(error.response.data).flat();

      toast({
        variant: "destructive",
        title: "Your request encountered the following errors:",
        description: (
          <>
            {Boolean(errorMessages?.length) ? (
              errorMessages.map((errorMessage: any, index: number) => (
                <p key={index}>- {capitalizeFirstLetter(errorMessage)}</p>
              ))
            ) : (
              <p>Sorry, an error occurred whilst creating businesses.</p>
            )}
          </>
        ),
      });
    },
  });

  const uploadBusinessDocumentMutation = useMutation({
    mutationKey: ["uploadBusinessDocument"],
    mutationFn: ({
      businessId,
      field,
      file,
    }: {
      businessId: string;
      field: BusinessDocumentField;
      file: File;
    }) => {
      const formData = new FormData();
      formData.append(field, file);
      return uploadBusinessDocs(businessId, formData);
    },
    onSuccess: async (_, variables) => {
      toast({
        description: `${businessDocumentLabelMap[variables.field]} uploaded successfully.`,
      });
      setUploadingDocumentField(null);
      await refetchSelectedBusiness();
      await refetchBusinesses();
    },
    onError: (error: any) => {
      setUploadingDocumentField(null);
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error?.response?.data?.message ??
          "We could not upload the selected document right now.",
      });
    },
  });

  const setDefaultBusinessMutation = useMutation({
    mutationKey: ["setDefaultBusiness"],
    mutationFn: (businessId: string) => setDefaultBusiness(businessId),
    onSuccess: async (_, businessId) => {
      toast({
        description: "Default business updated successfully.",
      });
      setSelectedBusinessId(businessId);
      setShowBusinessList(false);
      await refetchBusinesses();
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error?.response?.data?.error ??
          "We could not update the default business right now.",
      });
    },
  });

  const unsetDefaultBusinessMutation = useMutation({
    mutationKey: ["unsetDefaultBusiness"],
    mutationFn: (businessId: string) => unsetDefaultBusiness(businessId),
    onSuccess: async (_, businessId) => {
      toast({
        description: "Default business cleared.",
      });
      if (String(selectedBusinessId) === String(businessId)) {
        setSelectedBusinessId(businessId);
      }
      await refetchBusinesses();
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error?.response?.data?.error ??
          "We could not clear the default business right now.",
      });
    },
  });

  const handleSelectBusiness = (businessId: string) => {
    if (!businessId) {
      return;
    }

    setSelectedBusinessId(businessId);
    setShowBusinessList(false);
    setSelectedTransaction(null);
    setOpenTransferDetail(false);
  };

  const handleChangeBusiness = () => {
    setShowBusinessList(true);
  };

  const handleSetDefaultBusiness = (businessId: string) => {
    if (!businessId || setDefaultBusinessMutation.isPending) {
      return;
    }

    setDefaultBusinessMutation.mutate(businessId);
  };

  const triggerDocumentPicker = (field: BusinessDocumentField) => {
    documentInputRefs.current[field]?.click();
  };

  const handleDocumentFileChange = (
    field: BusinessDocumentField,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file || !selectedBusinessId || uploadBusinessDocumentMutation.isPending) {
      event.target.value = "";
      return;
    }

    setUploadingDocumentField(field);
    uploadBusinessDocumentMutation.mutate({
      businessId: selectedBusinessId,
      field,
      file,
    });
    event.target.value = "";
  };

  const handleUnsetDefaultBusiness = (businessId: string) => {
    if (!businessId || unsetDefaultBusinessMutation.isPending) {
      return;
    }

    unsetDefaultBusinessMutation.mutate(businessId);
  };

  const handleHistoryFilterChange = (partial: Partial<HistoryFilters>) => {
    setHistoryFilters((current) => ({ ...current, ...partial }));
    setHistoryPage(1);
  };

  const handleKycStepChange = (nextStep: number) => {
    setKycStep(Math.min(Math.max(nextStep, 1), totalKycSteps));
  };

  const handleExportHistoryCsv = async () => {
    if (!selectedBusinessId || isExportingHistory) {
      return;
    }

    try {
      setIsExportingHistory(true);
      const history = await getAllTransferHistory(userData?.id, {
        query: historyFilters.query || undefined,
        status: historyFilters.status,
        start_date: historyFilters.start_date || undefined,
        end_date: historyFilters.end_date || undefined,
        business_id: selectedBusinessId,
        wallet_numbers: businessWalletNumbers || undefined,
      });

      const transactions = (history?.results ?? []) as BusinessTransaction[];

      if (!transactions.length) {
        toast({
          description: "No transactions available to export.",
        });
        return;
      }

      const headers = [
        "Type",
        "Amount",
        "Currency",
        "Wallet Number",
        "Channel",
        "Mode",
        "Status",
        "Charges",
        "Reference",
        "Description",
        "Sender Wallet",
        "Receiver Wallet",
        "Sender ID",
        "Receiver ID",
        "Date",
      ];

      const rows = transactions.map((transaction) => [
        getTypeLabel(transaction),
        formatAmount(transaction.amount ?? 0),
        transaction.currency_code || "",
        getBusinessWalletNumber(transaction),
        transaction.channel || "",
        getModeLabel(transaction),
        transaction.status || "",
        formatAmount(transaction.charges ?? 0),
        transaction.reference || "",
        transaction.description || "",
        transaction.sender_wallet_number || "",
        transaction.receiver_wallet_number || "",
        transaction.sender_id || "",
        transaction.receiver_id || "",
        formatDate(transaction.created_at),
      ]);

      const csv = [headers, ...rows]
        .map((row) => row.map((value) => escapeCsvValue(value)).join(","))
        .join("\n");

      const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `business-history-${selectedBusinessId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to export business history.",
      });
    } finally {
      setIsExportingHistory(false);
    }
  };

  const handleOpenTransaction = (transaction: BusinessTransaction) => {
    setSelectedTransaction(transaction);
    setOpenTransferDetail(true);
  };

  const handleTransactionKeyDown = (
    event: { key: string; preventDefault: () => void },
    transaction: BusinessTransaction,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOpenTransaction(transaction);
    }
  };

  return (
    <Wrapper>
      {isPendingActivation && <Banner />}

      <div className="relative overflow-hidden rounded-[36px] border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-rose-50 p-5 shadow-[0_24px_70px_rgba(15,23,42,0.08)]">
        <div className="pointer-events-none absolute -right-16 top-8 h-40 w-40 rounded-full bg-rose-200/40 blur-3xl" />
        <div className="pointer-events-none absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-slate-200/50 blur-3xl" />

        <div className="relative space-y-6">
          <TitleWrapper className="!m-0">
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-rose-500">
              Business Center
            </p>
            <Title>Your Businesses</Title>
            <TitleDescription fontSize="0.92rem">
              Open one business at a time and use Change business when you want to switch.
            </TitleDescription>
          </TitleWrapper>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => setOpenInitiateTransfer(true)}
              className="flex w-full items-center gap-4 rounded-[26px] border border-slate-200 bg-white/85 px-4 py-4 text-left shadow-[0_16px_50px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_60px_rgba(15,23,42,0.08)] lg:max-w-[420px]"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-500 text-white">
                <SymbolIcon width={22} height={22} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Initiate Business Transfer</p>
                <p className="mt-1 text-xs text-slate-500">
                  Start a transfer for the currently selected business.
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setOpenBulkTransfer(true)}
              className="flex w-full items-center gap-4 rounded-[26px] border border-slate-200 bg-white/85 px-4 py-4 text-left shadow-[0_16px_50px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_60px_rgba(15,23,42,0.08)] lg:max-w-[420px]"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-500 text-white">
                <SymbolIcon width={22} height={22} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Bulk Business Transfer</p>
                <p className="mt-1 text-xs text-slate-500">
                  Pay several recipients at once from the selected business wallet.
                </p>
              </div>
            </button>

            <Button
              onClick={() => setShowAddBusiness(true)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-rose-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700"
            >
              <PlusIcon className="h-4 w-4" /> Add Business
            </Button>
          </div>

          {((userIsLoading && !userData) || (businessesIsLoading && !businessList.length)) ? (
            <LoadingContainer>
              <p>Loading businesses...</p>
            </LoadingContainer>
          ) : (userIsError || businessesIsError) && !businessList.length ? (
            <LoadingContainer>
              <p>An error occurred whilst loading businesses.</p>
              <button
                onClick={() => {
                  refetchUser();
                  refetchBusinesses();
                }}
                className="text-rose-600"
              >
                Click to retry
              </button>
            </LoadingContainer>
          ) : !businessList.length && !userIsLoading ? (
            <LoadingContainer>
              <div className="flex flex-col items-center gap-3 rounded-[28px] border border-dashed border-slate-200 bg-white/80 px-8 py-12 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                  <Building2 className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">No businesses found</h3>
                <p className="max-w-md text-sm text-slate-500">
                  Add a business to unlock transaction history, KYC details, and API keys.
                </p>
                <Button
                  onClick={() => setShowAddBusiness(true)}
                  className="mt-2 inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"
                >
                  <PlusIcon className="h-4 w-4" /> Add Business
                </Button>
              </div>
            </LoadingContainer>
          ) : (
            <>
              {(showBusinessList || !selectedBusiness) && (
              <section className="overflow-x-auto pb-2">
                <div className="flex min-w-max gap-4">
                  {businessList.map((business, index) => {
                    const isSelected = String(business.id) === selectedBusinessId;
                    const theme = businessThemes[index % businessThemes.length];
                    const verificationMeta = getBusinessVerificationMeta(business);

                    return (
                      <button
                        key={business.id}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => handleSelectBusiness(String(business.id))}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleSelectBusiness(String(business.id));
                          }
                        }}
                        className={`relative flex min-w-[285px] max-w-[320px] cursor-pointer flex-col justify-between overflow-hidden rounded-[28px] border bg-white p-5 text-left shadow-[0_18px_40px_rgba(15,23,42,0.08)] transition duration-200 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:ring-offset-2 focus:ring-offset-white ${
                          theme.border
                        } ${
                          isSelected ? "ring-2 ring-slate-900/10 ring-offset-2 ring-offset-white" : "opacity-95"
                        }`}
                      >
                        <div className={`absolute inset-x-0 top-0 h-1.5 ${theme.accentBar}`} />

                        <div className="relative flex h-full flex-col justify-between gap-6">
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-1">
                              <p className={`text-[11px] font-semibold uppercase tracking-[0.24em] ${theme.helper}`}>
                                Business
                              </p>
                              <h3 className="text-xl font-bold leading-tight text-slate-900">{business.name}</h3>
                              <p className={`text-sm ${theme.chipTone}`}>{business.email}</p>
                              <span
                                aria-label={`Verification status: ${verificationMeta.label}`}
                                className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ring-1 ${verificationMeta.tone}`}
                              >
                                <verificationMeta.Icon className="h-3.5 w-3.5" />
                                {verificationMeta.label}
                              </span>
                              {business.is_default ? (
                                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700 ring-1 ring-amber-200">
                                  <Star className="h-3.5 w-3.5 fill-current" />
                                  Default business
                                </span>
                              ) : null}
                            </div>

                            <div className={`flex h-11 w-11 items-center justify-center rounded-full ${theme.iconBg}`}>
                              <Building2 className="h-4 w-4" />
                            </div>
                          </div>

                          <div className="flex items-end justify-between gap-4">
                            <BusinessWalletPreview businessId={String(business.id)} theme={theme} />

                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${
                                isSelected
                                  ? "bg-slate-900 text-white"
                                  : "bg-slate-100 text-slate-500"
                              }`}
                            >
                              {isSelected ? "Selected" : "Select"}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
              )}

              {!showBusinessList && selectedBusiness ? (
                <section className="rounded-[32px] border border-slate-200 bg-white/85 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] backdrop-blur">
                  <>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="space-y-1">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                          Selected business
                        </p>
                        <h2 className="text-2xl font-bold text-slate-900">
                          {selectedBusinessDetailsView?.name || selectedBusiness.name}
                        </h2>
                        <p className="text-sm text-slate-500">
                          {selectedBusinessDetailsView?.email || selectedBusiness.email || "No email"}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          aria-label={`Verification status: ${selectedBusinessVerification.label}`}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ring-1 ${selectedBusinessVerification.tone}`}
                        >
                          <selectedBusinessVerification.Icon className="h-4 w-4" />
                          {selectedBusinessVerification.label}
                        </span>

                        {isDefaultBusiness ? (
                          <>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700 ring-1 ring-amber-200">
                              <Star className="h-4 w-4 fill-current" />
                              Default business
                            </span>

                            <Button
                              type="button"
                              variant="outline"
                              onClick={() =>
                                handleUnsetDefaultBusiness(String(selectedBusiness.id))
                              }
                              disabled={unsetDefaultBusinessMutation.isPending}
                              className="inline-flex items-center gap-2 rounded-full border-amber-200 bg-white px-4 py-2 text-sm font-semibold text-amber-700 shadow-sm transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                            >
                              <X className="h-4 w-4" />
                              {unsetDefaultBusinessMutation.isPending ? "Clearing..." : "Unset default"}
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleSetDefaultBusiness(String(selectedBusiness.id))}
                            disabled={setDefaultBusinessMutation.isPending}
                            className="inline-flex items-center gap-2 rounded-full border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 shadow-sm transition hover:border-amber-300 hover:bg-amber-100 hover:text-amber-800 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                          >
                            <Star className="h-4 w-4" />
                            {setDefaultBusinessMutation.isPending ? "Saving..." : "Set as default"}
                          </Button>
                        )}

                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleChangeBusiness}
                          className="inline-flex items-center gap-2 rounded-full border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-100 hover:text-rose-800"
                        >
                          <Replace className="h-4 w-4" />
                          Change business
                        </Button>
                      </div>
                    </div>

                    <SelectedBusinessWalletCards
                      businessId={String(selectedBusiness.id)}
                      theme={selectedBusinessTheme}
                    />

                    <div className="mt-5 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
                      {[
                        { key: "trade-profile", label: "Trade Profile", Icon: UserRound },
                        { key: "transactions", label: "Transaction History", Icon: HistoryIcon },
                        { key: "kyc", label: "KYC", Icon: ShieldCheck },
                        { key: "api-keys", label: "API Keys", Icon: KeyRound },
                      ].map(({ key, label, Icon }) => {
                        const isActive = activeTab === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setActiveTab(key as BusinessTab)}
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                              isActive
                                ? "bg-rose-600 text-white shadow-sm"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            {label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-5">
                      {activeTab === "trade-profile" ? (
                        <BusinessTradeProfile
                          business={selectedBusinessDetailsView}
                          user={userData}
                        />
                      ) : activeTab === "transactions" ? (
                        <section className="space-y-4">
                          <div className="rounded-[28px] border border-slate-200 bg-slate-50/70 p-4">
                            <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(190px,0.75fr)_minmax(170px,0.6fr)_minmax(170px,0.6fr)_auto]">
                              <div className="space-y-2">
                                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  Reference
                                </label>
                                <div className="relative">
                                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                  <Input
                                    value={historyFilters.query}
                                    onChange={(event) =>
                                      handleHistoryFilterChange({ query: event.target.value })
                                    }
                                    placeholder="Search by reference"
                                    className="pl-11"
                                  />
                                </div>
                              </div>

                              <div className="space-y-2">
                                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  Status
                                </label>
                                <Select
                                  value={historyFilters.status}
                                  onValueChange={(value) =>
                                    handleHistoryFilterChange({ status: value })
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select status" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {statusOptions.map(({ label, value }) => (
                                      <SelectItem key={value} value={value}>
                                        {label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="space-y-2">
                                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  Start date
                                </label>
                                <Input
                                  type="date"
                                  value={historyFilters.start_date}
                                  onChange={(event) =>
                                    handleHistoryFilterChange({ start_date: event.target.value })
                                  }
                                />
                              </div>

                              <div className="space-y-2">
                                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  End date
                                </label>
                                <Input
                                  type="date"
                                  value={historyFilters.end_date}
                                  onChange={(event) =>
                                    handleHistoryFilterChange({ end_date: event.target.value })
                                  }
                                />
                              </div>

                              <div className="flex items-end gap-2 xl:justify-end">
                                <Button
                                  type="button"
                                  onClick={() => {
                                    setHistoryFilters({
                                      query: "",
                                      status: "all",
                                      start_date: "",
                                      end_date: "",
                                    });
                                    setHistoryPage(1);
                                  }}
                                  variant="outline"
                                  className="inline-flex h-11 items-center gap-2 rounded-2xl border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                                >
                                  <RefreshCcw className="h-4 w-4" />
                                  Reset
                                </Button>

                                <Button
                                  type="button"
                                  onClick={handleExportHistoryCsv}
                                  disabled={isExportingHistory || historyRows.length === 0}
                                  className="inline-flex h-11 items-center gap-2 rounded-2xl border border-rose-600 bg-rose-600 px-4 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                                >
                                  <Download className="h-4 w-4" />
                                  {isExportingHistory ? "Exporting..." : "Export CSV"}
                                </Button>
                              </div>
                            </div>
                          </div>

                          <div className="rounded-[28px] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.04)]">
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="w-[150px]">Type</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Wallet</TableHead>
                                    <TableHead>Channel</TableHead>
                                    <TableHead>Mode</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Date</TableHead>
                                  </TableRow>
                                </TableHeader>

                                <TableBody>
                                  {historyIsLoading ? (
                                    <TableRow>
                                      <TableCell colSpan={7} className="py-12 text-center text-slate-500">
                                        Loading history...
                                      </TableCell>
                                    </TableRow>
                                  ) : historyIsError ? (
                                    <TableRow>
                                      <TableCell colSpan={7} className="py-12 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                          <p className="text-slate-600">Could not load history.</p>
                                          <Button
                                            type="button"
                                            onClick={() => refetchHistory()}
                                            className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
                                          >
                                            <RefreshCcw className="h-4 w-4" />
                                            Retry
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ) : historyRows.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={7} className="py-14 text-center text-slate-500">
                                        No transactions found for this business.
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    historyRows.map((transaction) => {
                                      const statusTone = getStatusTone(transaction.status);
                                      const isDebit =
                                        (transaction.debit_or_credit || "").toLowerCase() === "debit";
                                      const amount = formatAmount(transaction.amount ?? 0);

                                      return (
                                        <TableRow
                                          key={
                                            transaction.id ??
                                            `${transaction.reference ?? ""}-${transaction.created_at ?? ""}`
                                          }
                                          className="cursor-pointer"
                                          onClick={() => handleOpenTransaction(transaction)}
                                          onKeyDown={(event) => handleTransactionKeyDown(event, transaction)}
                                          tabIndex={0}
                                          role="button"
                                          aria-label={`Open transaction ${transaction.reference || transaction.id || ""}`}
                                        >
                                          <TableCell>
                                            <div className="flex flex-col">
                                              <span className="font-medium text-slate-900">
                                                {getTypeLabel(transaction)}
                                              </span>
                                              <span className="text-xs text-slate-500">
                                                {getReferenceLabel(transaction)}
                                              </span>
                                            </div>
                                          </TableCell>

                                          <TableCell>
                                            <div className="flex flex-col">
                                              <span
                                                className={`font-semibold ${
                                                  isDebit ? "text-rose-600" : "text-emerald-600"
                                                }`}
                                              >
                                                {isDebit ? "-" : "+"}
                                                {transaction.currency_code ? `${transaction.currency_code} ` : ""}
                                                {amount}
                                              </span>
                                              <span className="text-xs text-slate-500">
                                                Charges: {formatAmount(transaction.charges ?? 0)}
                                              </span>
                                            </div>
                                          </TableCell>

                                          <TableCell className="text-sm text-slate-700">
                                            {getBusinessWalletNumber(transaction)}
                                          </TableCell>

                                          <TableCell className="text-sm text-slate-500">
                                            {transaction.channel || "N/A"}
                                          </TableCell>

                                          <TableCell>
                                            <div className="flex items-center gap-2 font-medium text-slate-700">
                                              {isDebit ? (
                                                <ArrowDownRight className="h-4 w-4 text-rose-600" />
                                              ) : (
                                                <ArrowUpRight className="h-4 w-4 text-emerald-600" />
                                              )}
                                              {getModeLabel(transaction)}
                                            </div>
                                          </TableCell>

                                          <TableCell>
                                            <span
                                              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ring-1 ${statusTone}`}
                                            >
                                              {transaction.status || "Unknown"}
                                            </span>
                                          </TableCell>

                                          <TableCell className="text-sm text-slate-500">
                                            {formatDate(transaction.created_at)}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })
                                  )}
                                </TableBody>
                              </Table>
                            </div>
                          </div>

                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-sm text-slate-500">
                              {selectedBusiness?.name
                                ? `Browsing ${selectedBusiness.name}`
                                : "No business selected"}
                            </p>

                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => setHistoryPage((prevPage) => Math.max(prevPage - 1, 1))}
                                disabled={historyPage === 1 || !hasPrev}
                                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                                  historyPage === 1 || !hasPrev
                                    ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                                }`}
                              >
                                <ChevronLeft className="h-4 w-4" />
                                Previous
                              </button>

                              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">
                                <Clock3 className="h-4 w-4" />
                                Page {historyPage} of {totalPages}
                              </span>

                              <button
                                type="button"
                                onClick={() => setHistoryPage((prevPage) => prevPage + 1)}
                                disabled={!hasNext}
                                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                                  !hasNext
                                    ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                                    : "border-rose-600 bg-rose-600 text-white hover:bg-rose-700"
                                }`}
                              >
                                Next
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </section>
                      ) : activeTab === "kyc" ? (
                        <section className="space-y-5">
                          {selectedBusinessLoading && !selectedBusinessDetailsView ? (
                            <LoadingContainer>
                              <p>Loading KYC details...</p>
                            </LoadingContainer>
                          ) : selectedBusinessError && !selectedBusinessDetailsView ? (
                            <LoadingContainer>
                              <p>Could not load KYC details.</p>
                              <button onClick={() => refetchSelectedBusiness()} className="text-rose-600">
                                Click to retry
                              </button>
                            </LoadingContainer>
                          ) : (
                            <>
                              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                <div className="max-w-3xl space-y-2">
                                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-rose-500">
                                    KYC
                                  </p>
                                  <h3 className="text-2xl font-bold text-slate-900">Business profile</h3>
                                  <p className="text-sm text-slate-500">
                                    View the business record and document coverage for the selected business.
                                  </p>
                                </div>

                                <Button
                                  type="button"
                                  onClick={() => navigate(`/home/business/${selectedBusinessId}`)}
                                  className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"
                                >
                                  Open full KYC page
                                </Button>
                              </div>

                              <div className="space-y-4">
                                <div className="grid gap-3 md:grid-cols-3">
                                  {kycStepItems.map((step, index) => {
                                    const isActive = kycStep === step.id;
                                    const isComplete = kycStep > step.id;
                                    const StepIcon = step.icon;

                                    return (
                                      <button
                                        key={step.id}
                                        type="button"
                                        onClick={() => handleKycStepChange(step.id)}
                                        className={`rounded-[24px] border p-4 text-left transition ${
                                          isActive
                                            ? "border-rose-300 bg-rose-50 shadow-[0_12px_30px_rgba(244,63,94,0.12)]"
                                            : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                                        }`}
                                      >
                                        <div className="flex items-start gap-3">
                                          <div
                                            className={`flex h-10 w-10 items-center justify-center rounded-2xl ${
                                              isActive
                                                ? "bg-rose-600 text-white"
                                                : isComplete
                                                  ? "bg-emerald-50 text-emerald-700"
                                                  : "bg-slate-100 text-slate-500"
                                            }`}
                                          >
                                            <StepIcon className="h-4 w-4" />
                                          </div>
                                          <div className="min-w-0">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                              Step {index + 1}
                                            </p>
                                            <h4 className="mt-1 text-sm font-bold text-slate-900">{step.title}</h4>
                                            <p className="mt-1 text-xs text-slate-500">{step.description}</p>
                                          </div>
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>

                                <div className="rounded-[28px] border border-slate-200 bg-slate-50/70 p-5">
                                  {kycStep === 1 ? (
                                    <div className="space-y-5">
                                      <div className="grid gap-4 md:grid-cols-2">
                                        {[
                                          ["Business name", selectedBusinessDetailsView?.name || "N/A"],
                                          ["Email", selectedBusinessDetailsView?.email || "N/A"],
                                          ["Phone", selectedBusinessDetailsView?.phone || "N/A"],
                                          ["Country", selectedBusinessDetailsView?.country || "N/A"],
                                          ["Type", selectedBusinessDetailsView?.type || "N/A"],
                                          ["Size", selectedBusinessDetailsView?.size || "N/A"],
                                          ["Address", selectedBusinessDetailsView?.address || "N/A"],
                                          [
                                            "Monthly volume",
                                            selectedBusinessDetailsView?.transaction_volume || "N/A",
                                          ],
                                          [
                                            "Director I",
                                            selectedBusinessDetailsView?.id_director_one || "N/A",
                                          ],
                                          [
                                            "Director II",
                                            selectedBusinessDetailsView?.id_director_two || "N/A",
                                          ],
                                        ].map(([label, value]) => (
                                          <div
                                            key={label}
                                            className="rounded-2xl border border-slate-200 bg-white p-4"
                                          >
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                              {label}
                                            </p>
                                            <p className="mt-2 text-sm font-semibold text-slate-900">
                                              {String(value)}
                                            </p>
                                          </div>
                                        ))}
                                      </div>

                                      <div className="flex items-center justify-between gap-3">
                                        <p className="text-sm text-slate-500">
                                          Confirm the business profile before moving to documents.
                                        </p>
                                        <Button
                                          type="button"
                                          onClick={() => handleKycStepChange(2)}
                                          className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"
                                        >
                                          Continue
                                        </Button>
                                      </div>
                                    </div>
                                  ) : kycStep === 2 ? (
                                    <div className="space-y-5">
                                      <div className="flex items-start justify-between gap-3">
                                        <div>
                                          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-rose-500">
                                            Documents
                                          </p>
                                          <h4 className="mt-2 text-xl font-bold text-slate-900">
                                            {documentsUploaded} of {documentCards.length} uploaded
                                          </h4>
                                          <p className="mt-1 text-sm text-slate-500">
                                            Uploaded business documents and their current status.
                                          </p>
                                        </div>
                                        <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                                          <BadgeCheck className="h-4 w-4" />
                                          KYC ready
                                        </span>
                                      </div>

                                      <div className="space-y-3">
                                        {documentCards.map((documentCard) => {
                                          const hasDocument = Boolean(documentCard.value);
                                          const isUploading =
                                            uploadingDocumentField === documentCard.field &&
                                            uploadBusinessDocumentMutation.isPending;

                                          return (
                                            <div
                                              key={documentCard.label}
                                              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300"
                                            >
                                              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                                <div className="space-y-1">
                                                  <p className="text-sm font-semibold text-slate-900">
                                                    {documentCard.label}
                                                  </p>
                                                  <p className="text-xs text-slate-500">
                                                    {documentCard.description}
                                                  </p>
                                                </div>

                                                <div className="flex flex-wrap items-center gap-2">
                                                  <span
                                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                                      hasDocument
                                                        ? "bg-emerald-50 text-emerald-700"
                                                        : "bg-slate-100 text-slate-500"
                                                    }`}
                                                  >
                                                    {hasDocument ? "Uploaded" : "Missing"}
                                                  </span>

                                                  {hasDocument ? (
                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        window.open(
                                                          `${REACT_APP_BASE_URL}${documentCard.value}`,
                                                          "_blank",
                                                        )
                                                      }
                                                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                                    >
                                                      <FileText className="h-3.5 w-3.5" />
                                                      View
                                                    </button>
                                                  ) : null}

                                                  <Button
                                                    type="button"
                                                    onClick={() => triggerDocumentPicker(documentCard.field)}
                                                    disabled={uploadBusinessDocumentMutation.isPending}
                                                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white ${
                                                      hasDocument
                                                        ? "bg-rose-600 hover:bg-rose-700"
                                                        : "bg-slate-900 hover:bg-slate-800"
                                                    }`}
                                                  >
                                                    <Upload className="h-3.5 w-3.5" />
                                                    {isUploading
                                                      ? "Uploading..."
                                                      : hasDocument
                                                        ? "Replace upload"
                                                        : "Upload file"}
                                                  </Button>

                                                  <input
                                                    ref={(node) => {
                                                      documentInputRefs.current[documentCard.field] = node;
                                                    }}
                                                    type="file"
                                                    accept=".pdf,.png,.jpg,.jpeg,.webp,image/*"
                                                    className="hidden"
                                                    onChange={(event) =>
                                                      handleDocumentFileChange(documentCard.field, event)
                                                    }
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>

                                      <div className="flex items-center justify-between gap-3">
                                        <Button
                                          type="button"
                                          variant="outline"
                                          onClick={() => handleKycStepChange(1)}
                                          className="inline-flex items-center gap-2 rounded-full border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                                        >
                                          Back
                                        </Button>
                                        <Button
                                          type="button"
                                          onClick={() => handleKycStepChange(3)}
                                          className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"
                                        >
                                          Continue
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="space-y-5">
                                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                        <div className="max-w-2xl">
                                          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-rose-500">
                                            Final review
                                          </p>
                                          <h4 className="mt-2 text-2xl font-bold text-slate-900">
                                            Complete the verification flow
                                          </h4>
                                          <p className="mt-2 text-sm text-slate-500">
                                            Review the business profile, document coverage, and verification
                                            status before opening the full KYC page.
                                          </p>
                                        </div>

                                        <span
                                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${selectedBusinessVerification.tone}`}
                                        >
                                          <selectedBusinessVerification.Icon className="h-4 w-4" />
                                          {selectedBusinessVerification.label}
                                        </span>
                                      </div>

                                      <div className="grid gap-4 md:grid-cols-3">
                                        {[
                                          ["Profile", "Reviewed"],
                                          ["Documents", `${documentsUploaded}/${documentCards.length} uploaded`],
                                          ["Business", selectedBusinessDetailsView?.status || "Pending"],
                                        ].map(([label, value]) => (
                                          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                              {label}
                                            </p>
                                            <p className="mt-2 text-sm font-semibold text-slate-900">{value}</p>
                                          </div>
                                        ))}
                                      </div>

                                      <div className="flex flex-wrap items-center justify-between gap-3">
                                        <Button
                                          type="button"
                                          variant="outline"
                                          onClick={() => handleKycStepChange(2)}
                                          className="inline-flex items-center gap-2 rounded-full border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                                        >
                                          Back
                                        </Button>

                                        <Button
                                          type="button"
                                          onClick={() => navigate(`/home/business/${selectedBusinessId}`)}
                                          className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"
                                        >
                                          Open full KYC page
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                        </section>
                      ) : (
                        <MYAPIs businessId={selectedBusinessId} hideBusinessSelector />
                      )}
                    </div>
                  </>
                </section>
              ) : null}
            </>
          )}
        </div>
      </div>

      <InitiateTransfer
        open={openInitiateTransfer}
        handleClose={() => setOpenInitiateTransfer(false)}
        businesses={businessList}
        isLoadingBusiness={businessesIsLoading}
        isBusinessTransfer={true}
      />

      <BulkTransfer
        open={openBulkTransfer}
        handleClose={() => setOpenBulkTransfer(false)}
        businesses={businessList}
        isBusinessTransfer={true}
      />

      <AddBusiness
        open={showAddBusiness}
        handleClose={() => setShowAddBusiness(false)}
        countries={countries}
        countriesIsLoading={countriesIsLoading}
        createBusinessMutation={createBusinessMutation}
        isLoading={createBusinessMutation.isPending}
        businessTypes={businessTypes}
        businessTypesIsLoading={businessTypesIsLoading}
      />

      <TransferDetails
        open={openTransferDetail}
        handleClose={() => setOpenTransferDetail(false)}
        details={selectedTransaction}
      />
    </Wrapper>
  );
};

export default Business;
